import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { isEmpty, keyBy, pick } from 'lodash';
import { set } from 'lodash/fp';
import { useToggle } from 'react-use';
import qs from 'qs';

import TenantUserPage from '../hocs/TenantUserPage';
import useAppTitle from '../hooks/useAppTitle';
import useLocale from '../hooks/useLocale';
import useQueryParams from '../hooks/useQueryParams';
import firebase from '../../firebase';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import NewSurveyAnswerForm from '../forms/NewSurveyAnswerForm';
import NewSurveyAnswerConfirmForm from '../forms/NewSurveyAnswerConfirmForm';
import { getDocumentData, getCollectionData } from '../../shared/firebase';
import { computeSupportRequired } from '../../shared/models/survey';
import { updateQuestionStatistics } from '../../util';

const { entries } = Object;
const storageRef = firebase.storage().ref();
const db = firebase.firestore();

function NewSurveyAnswer(props) {
  const {
    history,
    location,
    user,
    toggleLoginForm,
    match: {
      params: { tenantPath, surveyId },
    },
    setLang,
    translate,
  } = props;
  const queryParams = useQueryParams();
  const { tag = null, eventId, entryId, frameIndex } = queryParams;
  const [activeIndex, setActiveIndex] = useState(0);
  const survey = useDocumentSubscription(db.collection('surveys').doc(surveyId), [surveyId]);
  const steps = survey?.steps ?? [{}];
  const questions = useCollectionSubscriptionInTenant(db.collection('questions'), [], { initialItems: null });
  const entry = useDocumentSubscription(
    eventId && entryId ? db.doc(`events/${eventId}/entries/${entryId}`) : null,
    [eventId, entryId]
  );
  const lecture = useDocumentSubscription(
    eventId && entry?.frames?.[frameIndex]?.lectureId
      ? db.doc(`events/${eventId}/lectures/${entry.frames[frameIndex].lectureId}`)
      : null,
    [eventId, entry?.frames?.[frameIndex]?.lectureId]
  );
  const questionsById = keyBy(questions, 'id');
  const [showsConfirm, toggleConfirm] = useToggle(false);
  const [values, setValues] = useState({});
  const onSubmitNewSurveyAnswerForm = async (_values) => {
    setValues({ ...values, ..._values });
    toggleConfirm(true);
  };
  const onSubmitConfirm = async ({ answers: _answers }) => {
    try {
      const ref = db.collection('surveyAnswers').doc();
      const answers = await entries(_answers).reduce(async (x, [k, v]) => {
        const prevs = await x;

        const question = questionsById[k];
        return {
          ...prevs,
          [k]:
            question?.type === 'imageFile' && !isEmpty(v)
              ? await Promise.all(
                  v.map(async (file) => {
                    const fileRef = storageRef.child(`surveyAnswers/${ref.id}/${k}/${new Date().toISOString()}/${file.name}`);
                    await fileRef.put(file, { contentType: file.type });
                    return {
                      ...pick(file, ['name', 'type']),
                      url: await fileRef.getDownloadURL(),
                    };
                  })
                )
              : v ?? null,
        };
      }, Promise.resolve({}));

      const supportRequired = computeSupportRequired(survey, _answers);

      // 事前アンケート回答を取得して統合
      const combinedAnswers = eventId && entryId && frameIndex != null
        ? { ...entry.answers, ...answers }
        : answers;

      await ref.set({
        tenantId: tenantPath,
        answers: combinedAnswers,
        surveyId,
        tag,
        status: supportRequired ? 'supportRequired' : 'initial',
        createdAt: new Date(),
        createdBy: user,
      });

      if (eventId && entryId && frameIndex != null) {
        const entryRef = db.doc(`events/${eventId}/entries/${entryId}`);
        const entry = await getDocumentData(entryRef);
        const entryFrames = set(`${frameIndex}.laterAnswers`, combinedAnswers, entry.frames);
        await entryRef.update({ frames: entryFrames });
      }

      // NOTE: 質問統計の更新
      await updateQuestionStatistics(answers);

      history.replace(`/${tenantPath}/surveys/${survey.id}/surveyAnswers/thanks`);
    } catch (error) {
      console.error(error);
      toast.error('失敗しました');
    }
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [showsConfirm, activeIndex]);
  useEffect(() => {
    survey?.lang && setLang(survey.lang);
  }, [survey?.lang]);
  useEffect(() => {
    if(survey?.enablesSimpleUserRegistration) {
      history.replace(location.pathname + '?' + qs.stringify({ ...queryParams, simpleUserRegistration: 1 }));
    }
  }, [survey?.enablesSimpleUserRegistration]);
  useAppTitle(survey?.title);

  return (
    survey != null &&
    questions != null && (
      <div className="new-survey-answer position-relative">
        {!showsConfirm ? (
          <NewSurveyAnswerForm
            translate={translate}
            onSubmit={onSubmitNewSurveyAnswerForm}
            values={values}
            user={user}
            onClickLogin={toggleLoginForm}
            {...{ survey, questions, steps, activeIndex, setActiveIndex, }}
          />
        ) : translate(
          <NewSurveyAnswerConfirmForm
            values={values}
            onSubmit={onSubmitConfirm}
            onClickBack={toggleConfirm.bind(null, false)}
            {...{ user, survey, steps, questions }}
          />
        )}
      </div>
    )
  );
}

NewSurveyAnswer.preview = true;
NewSurveyAnswer.initiallyOpenLogin = true;
export default TenantUserPage(NewSurveyAnswer);
